/**
 * Create-project modal.
 *
 * Default stack is **Doable Standard** (the Applox-DNA Kobold-derived
 * scaffold with SST analytics, optimistic locking, JWT+RBAC, etc.) —
 * shown as a selected card, no picker. Power users can expand the
 * Advanced disclosure to choose a legacy stack.
 */
import { useState, type FormEvent } from 'react'
import type { ProjectDto, ProjectStack } from '@doable/shared-types'
import { api, type ApiError } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { Dialog } from './ui/dialog'
import { Button } from './ui/button'
import { FloatingInput, FloatingTextarea } from './ui/input'
import { StatusBox } from './ui/badge'

const DEFAULT_STACK: ProjectStack = 'doable-standard'

const LEGACY_STACKS: { value: ProjectStack; label: string; note: string }[] = [
  { value: 'nextjs-pg', label: 'Bare Next.js + Postgres', note: 'No SST, no admin shell, no Prisma' },
  { value: 'react-node-pg', label: 'React + Node + Postgres', note: 'No Next.js' },
  { value: 'sveltekit-pg', label: 'SvelteKit + Postgres', note: '' },
  { value: 'nuxt-pg', label: 'Nuxt + Postgres', note: '' },
  { value: 'vue-node-pg', label: 'Vue + Node + Postgres', note: '' },
  { value: 'astro-pg', label: 'Astro + Postgres', note: '' },
]

const PROMPT_EXAMPLES = [
  'Landing page for a Berlin fitness studio: hero, classes, pricing, contact form.',
  'Internal tool for a 12-person team: members, weekly check-ins, simple admin.',
  'Coming-soon page with email signup, countdown timer, and centered logo.',
]

type Props = {
  onClose: () => void
  onCreated: (project: ProjectDto) => void
}

const FORM_ID = 'new-project-form'

export function NewProjectModal({ onClose, onCreated }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [initialPrompt, setInitialPrompt] = useState('')
  const [stack, setStack] = useState<ProjectStack>(DEFAULT_STACK)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!accessToken) return
    setError(null)
    setSubmitting(true)
    try {
      const project = await api.createProject(accessToken, {
        name,
        description: description || undefined,
        initialPrompt,
        stack,
      })
      onCreated(project)
    } catch (err) {
      setError((err as ApiError).title ?? 'Failed to create project')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title="New project"
      description="Doable will scaffold the Applox standard stack and start building."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create project'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="space-y-4 pt-1" onSubmit={onSubmit}>
        <FloatingInput
          label="Project name"
          type="text"
          required
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <FloatingInput
          label="Description (optional)"
          type="text"
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div>
          <FloatingTextarea
            label="What should it do?"
            required
            rows={4}
            minLength={1}
            maxLength={4000}
            value={initialPrompt}
            onChange={(e) => setInitialPrompt(e.target.value)}
            hint="The assistant explores in Plan Mode first and proposes a few starting points."
            className="resize-none"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {PROMPT_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setInitialPrompt(example)}
                className="rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {example.slice(0, 60)}…
              </button>
            ))}
          </div>
        </div>

        {/* Stack — Doable Standard selected by default; Advanced reveals legacy options */}
        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Stack</p>
          {!showAdvanced ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-foreground/30 bg-volt-soft px-3 py-2">
              <div>
                <div className="text-sm font-medium text-volt-ink">Doable Standard</div>
                <div className="text-xs text-muted-foreground">
                  Next.js · Prisma · Server-Side Tracking · 4-role admin · Sentry
                </div>
              </div>
              <Button variant="link" size="xs" onClick={() => setShowAdvanced(true)}>
                change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-foreground/30 bg-volt-soft p-3">
                <input
                  type="radio"
                  name="stack"
                  value="doable-standard"
                  checked={stack === 'doable-standard'}
                  onChange={() => setStack('doable-standard')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-volt-ink">Doable Standard</div>
                  <div className="text-xs text-muted-foreground">
                    Recommended. Next.js + Prisma + SST analytics + admin + Sentry.
                  </div>
                </div>
              </label>
              {LEGACY_STACKS.map((s) => (
                <label
                  key={s.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="stack"
                    value={s.value}
                    checked={stack === s.value}
                    onChange={() => setStack(s.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-foreground">{s.label}</div>
                    {s.note && <div className="text-xs text-muted-foreground">{s.note}</div>}
                  </div>
                </label>
              ))}
              <Button
                variant="link"
                size="xs"
                onClick={() => {
                  setStack('doable-standard')
                  setShowAdvanced(false)
                }}
              >
                ← back to recommended
              </Button>
            </div>
          )}
        </div>

        {error && <StatusBox tone="destructive">{error}</StatusBox>}
      </form>
    </Dialog>
  )
}
